import React, { useState, useMemo } from 'react';
import { useMyPayments } from '../../hooks/usePaymentQueries';
import { useAuth } from '../../hooks/useAuth';
import PaymentStatusBadge from './PaymentStatusBadge';
import PaymentModal from './PaymentModal';
import PaymentDetails from './PaymentDetails';
import { format } from 'date-fns';
import './PaymentHistory.css';

const PaymentHistory = ({ filters = {} }) => {
  const { data: paymentsResponse, isLoading, error } = useMyPayments(filters);
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedPaymentId, setExpandedPaymentId] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // Handle different response structures (array directly or wrapped in data property)
  const payments = useMemo(() => {
    if (!paymentsResponse) return null;
    // If response is an array, use it directly
    if (Array.isArray(paymentsResponse)) return paymentsResponse;
    // If response has a data property, use that
    if (paymentsResponse.data && Array.isArray(paymentsResponse.data)) return paymentsResponse.data;
    // If response has a results property (pagination), use that
    if (paymentsResponse.results && Array.isArray(paymentsResponse.results)) return paymentsResponse.results;
    // Otherwise return empty array
    return [];
  }, [paymentsResponse]);

  const isEmployer = user?.role_name === 'employer';
  const isEmployee = user?.role_name === 'employee';

  // Backend should filter payments by role (employee_id for employees, employer_id for employers)
  // We trust the backend filtering and only apply status filter on frontend
  const filteredPayments = useMemo(() => {
    if (!payments || !Array.isArray(payments)) return [];

    if (statusFilter === 'all') return payments;

    const normalizeStatus = (status) => (status || '').toString().toLowerCase().trim();
    const isPendingStatus = (status) => normalizeStatus(status).includes('pending');
    const isPaidStatus = (status) => normalizeStatus(status).includes('paid');

    return payments.filter(payment => {
      const employerStatus = payment.employer_payment_status;
      const employeeStatus = payment.employee_payout_status;

      if (statusFilter === 'pending') {
        if (isEmployer) return isPendingStatus(employerStatus);
        if (isEmployee) return isPendingStatus(employeeStatus);
        return isPendingStatus(employerStatus) || isPendingStatus(employeeStatus);
      }

      if (statusFilter === 'paid') {
        if (isEmployer) return isPaidStatus(employerStatus);
        if (isEmployee) return isPaidStatus(employeeStatus);
        return isPaidStatus(employerStatus) || isPaidStatus(employeeStatus);
      }

      return true;
    });
  }, [payments, statusFilter, user, isEmployer, isEmployee]);

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const handleToggleExpand = (paymentId) => {
    setExpandedPaymentId(expandedPaymentId === paymentId ? null : paymentId);
  };

  const handleOpenPaymentModal = (e, payment) => {
    e.stopPropagation();
    setSelectedPayment(payment);
    setPaymentModalOpen(true);
  };

  const handleClosePaymentModal = () => {
    setPaymentModalOpen(false);
    setSelectedPayment(null);
  };


  if (isLoading) {
    return (
      <div className="payment-history payment-history--loading">
        <div className="payment-history__spinner"></div>
        <p>Loading payment history...</p>
      </div>
    );
  }

  if (error) {
    console.error('Payment history error:', error);
    return (
      <div className="payment-history payment-history--error">
        <p>Error loading payment history. Please try again.</p>
        {process.env.NODE_ENV === 'development' && (
          <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
            Error details: {error?.message || JSON.stringify(error)}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="payment-history">
      <div className="payment-history__header">
        <h2 className="payment-history__title">Payment History</h2>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="payment-history__filter"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      {(!filteredPayments || filteredPayments.length === 0) ? (
        <div className="payment-history__empty-state">
          <p>
            {isLoading ? 'Loading payment history...' : 
             isEmployee ? 'No payment history found. Your completed jobs will appear here once payments are processed.' :
             isEmployer ? 'No payment history found. Payments for your job postings will appear here once created.' :
             'No payments found.'}
          </p>
        </div>
      ) : (
        <div className="payment-history__list">
          {filteredPayments.map((payment) => {
          const isExpanded = expandedPaymentId === payment.id;
          // Show payment button for employers with pending payments
          // Check if status indicates pending payment (not paid)
          const isPending = payment.employer_payment_status !== 'employer_paid' && 
                           payment.employer_payment_status !== 'failed' &&
                           payment.employer_payment_status;
          // Show button for any pending payment (payment link will be fetched in modal if needed)
          const showPaymentLink = isEmployer && isPending;

          return (
            <div key={payment.id} className="payment-history__item">
              <div 
                className="payment-history__item-main"
                onClick={() => handleToggleExpand(payment.id)}
              >
                <div className="payment-history__item-left">
                  <div className="payment-history__item-info">
                    <h3 className="payment-history__item-job">
                      {payment.job_role_name || 
                       payment.job_posting_detail?.job_role_detail?.job_role_name ||
                       payment.job_posting?.job_role_detail?.job_role_name ||
                       payment.job_posting?.job_role?.job_role_name || 
                       'Job Payment'}
                    </h3>
                    <div className="payment-history__item-meta">
                      <span className="payment-history__item-date">
                        {formatDate(payment.job_posting_date || 
                                   payment.job_posting_detail?.date || 
                                   payment.job_posting?.date || 
                                   payment.job_date)}
                      </span>
                      {payment.id && (
                        <span className="payment-history__item-id">
                          Payment #{payment.id}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="payment-history__item-status">
                    {/* For employers: show employer payment status */}
                    {isEmployer && (
                      <PaymentStatusBadge 
                        status={payment.employer_payment_status} 
                        type="employer"
                      />
                    )}
                    {/* For employees: show employee payout status */}
                    {isEmployee && (
                      <PaymentStatusBadge 
                        status={payment.employee_payout_status} 
                        type="employee"
                      />
                    )}
                    {/* For admins or other roles: show both */}
                    {!isEmployer && !isEmployee && (
                      <>
                        <PaymentStatusBadge 
                          status={payment.employer_payment_status} 
                          type="employer"
                        />
                        <PaymentStatusBadge 
                          status={payment.employee_payout_status} 
                          type="employee"
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="payment-history__item-right">
                  <div className="payment-history__item-amount">
                    <span className="payment-history__amount-value">
                      {isEmployee 
                        ? formatCurrency(payment.employee_payout_amount || payment.total_amount || payment.amount)
                        : formatCurrency(payment.total_amount || payment.amount)
                      }
                    </span>
                  </div>
                  {showPaymentLink && (
                    <button
                      className="payment-history__pay-button"
                      onClick={(e) => handleOpenPaymentModal(e, payment)}
                      title="Pay Now"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                        <line x1="2" y1="10" x2="22" y2="10"></line>
                      </svg>
                      Pay Now
                    </button>
                  )}
                  <div className="payment-history__expand-icon">
                    {isExpanded ? '▼' : '▶'}
                  </div>
                </div>
              </div>

              {/* Expanded payment details */}
              {isExpanded && (
                <div className="payment-history__expanded-details">
                  <PaymentDetails 
                    paymentId={payment.id} 
                    onOpenPaymentModal={showPaymentLink ? () => handleOpenPaymentModal(null, payment) : null} 
                  />
                </div>
              )}
            </div>
          );
          })}
        </div>
      )}

      {/* Payment Modal */}
      {selectedPayment && (
        <PaymentModal
          isOpen={paymentModalOpen}
          onClose={handleClosePaymentModal}
          paymentLink={selectedPayment.payment_link_url || selectedPayment.payment_link}
          paymentId={selectedPayment.id}
          status={selectedPayment.employer_payment_status}
        />
      )}
    </div>
  );
};

export default PaymentHistory;

